import { JobSearchGrade } from '@/types/jobs';
import React, { useEffect, useRef, useState } from 'react';

import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import styles from './GradeSelect.module.scss';

export interface GradeSelectProps {
  value: JobSearchGrade | undefined;
  onChange: (value: JobSearchGrade | undefined) => void;
  placeholder?: string;
  id?: string;
}

const GRADE_OPTIONS: JobSearchGrade[] = [
  'Resident Medical Officer',
  'Registrar',
  'SMO/MOSS',
  'Specialist Consultant',
  'Clinical Director',
  'Director of Medical Services',
];

export const GradeSelect = ({
  value,
  onChange,
  placeholder = "I'm a...",
  id = 'grade-select',
}: GradeSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option: JobSearchGrade) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleButtonKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsOpen(true);
        break;
    }
  };

  const displayValue = value ?? placeholder;
  const labelId = `${id}-label`;
  const buttonId = `${id}-button`;
  const listboxId = `${id}-listbox`;

  return (
    <div className={styles.gradeSelect} ref={dropdownRef}>
      <label className={styles.label} id={labelId} htmlFor={buttonId}>
        Grade
      </label>
      <button
        id={buttonId}
        type="button"
        className={styles.selectButton}
        onClick={handleToggle}
        onKeyDown={handleButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={labelId}
        aria-describedby={value ? undefined : `${id}-placeholder`}
      >
        <span className={styles.selectText}>{displayValue}</span>
        {isOpen ? <ExpandLess className={styles.arrow} /> : <ExpandMore className={styles.arrow} />}
      </button>

      {isOpen && (
        <ul className={styles.dropdown} role="listbox" id={listboxId} aria-labelledby={labelId}>
          {GRADE_OPTIONS.map(option => (
            <li
              key={option}
              className={styles.option}
              role="option"
              aria-selected={value === option}
            >
              <button
                type="button"
                className={styles.optionButton}
                onClick={() => handleOptionSelect(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOptionSelect(option);
                  }
                }}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GradeSelect;
